import { FileKey } from "lucide-react";
import { PortalLayout } from "../components/PortalLayout";
import { platformLogoMap } from "../config/platforms";

const solutions = [
  {
    name: "FPI",
    logoKey: "FPI",
    tagline: "Transaction Management System",
    description:
      "A secure transaction management platform designed to support structured financial operations and high-volume payment processing environments.",
    capabilities: [
      "Secure transaction processing",
      "Financial account and payment management",
      "Audit-ready financial tracking",
      "Scalable architecture for high-volume environments",
    ],
    value:
      "FPI enables organisations to manage financial operations with greater control, transparency, and scalability, particularly in environments requiring high transaction throughput.",
  },
  {
    name: "BOS Membership",
    logoKey: "BOS",
    tagline: "Business Operations & Workforce Management Platform",
    description:
      "A structured platform focused on improving internal business operations, workforce management, and organisational productivity.",
    capabilities: [
      "Employee and workforce management",
      "Productivity tracking and reporting",
      "Internal process optimisation",
      "Structured membership and access control",
    ],
    value:
      "BOS Membership provides organisations with tools to improve efficiency, monitor performance, and streamline internal operations within a controlled environment.",
  },
  {
    name: "LSD Membership",
    logoKey: "Leo Membership",
    tagline: "Membership & Subscription Management Platform",
    description:
      "A flexible platform designed to manage subscriptions, user access, and service delivery across digital environments.",
    capabilities: [
      "Subscription and billing management",
      "User lifecycle and access control",
      "Integration with multiple platforms",
      "Scalable membership structures",
    ],
    value:
      "LSD Membership enables businesses to build recurring revenue models through structured subscription systems while maintaining control over user access and engagement.",
  },
  {
    name: "PICME",
    logoKey: "PicMe",
    tagline: "Digital Engagement & Interaction Platform",
    description:
      "A platform designed to facilitate structured interaction between users and service providers within a controlled and monitored environment.",
    capabilities: [
      "User engagement and communication workflows",
      "Structured interaction tracking",
      "Controlled access environments",
      "Integration with service delivery systems",
    ],
    value:
      "PICME enhances engagement and communication efficiency, allowing organisations to manage user interactions in a structured and traceable manner.",
  },
  {
    name: "EvidenceWeb",
    logoKey: "Evidence Web",
    tagline: "Digital Evidence Management & Chain-of-Custody Platform",
    description:
      "A specialised platform designed to manage the capture, storage, and verification of evidence within operational and legal environments.",
    capabilities: [
      "Evidence capture and documentation",
      "Chain-of-custody tracking",
      "Case-based evidence management",
      "Audit trails and verification systems",
      "Secure storage and controlled access",
    ],
    value:
      "EvidenceWeb ensures the integrity, traceability, and admissibility of evidence by providing a secure, structured system that supports compliance and operational accountability.",
  },
];

export function LicensedSolutionsPage() {
  return (
    <PortalLayout>

      {/* Header */}
      <div className="mb-14">
        <div className="flex items-center gap-3 mb-4">
          <FileKey className="w-5 h-5 text-[#7B1A1A]" />
          <p className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A]">
            Licensed & Partner Solutions
          </p>
        </div>
        <h1 className="text-2xl sm:text-4xl md:text-5xl font-semibold text-[#1F2933] mb-8 leading-tight max-w-3xl">
          Extended Capabilities Through Licensed Technologies
        </h1>
        <p className="text-[#4B5563] max-w-4xl leading-relaxed">
          In addition to its proprietary platforms, LEO Group distributes and supports selected licensed and partner
          technologies. These solutions are integrated into the group's ecosystem and extend its capabilities across
          financial, operational, and security environments.
        </p>
      </div>

      {/* Info */}
      <div className="bg-[#FDEAE0]/60 border border-[#7B1A1A]/15 rounded-lg p-6 mb-14">
        <p className="text-sm text-[#4B5563] leading-relaxed">
          These licensed solutions complement LEO Group's owned platforms and enable the group to deliver comprehensive
          technology offerings across multiple sectors without requiring direct development of every component.
        </p>
      </div>

      {/* Solutions Grid */}
      <div className="mb-14">
        <h2 className="text-xl font-semibold text-[#1F2933] mb-8">Solution Portfolio</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {solutions.map((solution) => {
            const logoSrc = platformLogoMap[solution.logoKey];
            return (
              <div
                key={solution.name}
                className={`bg-white border border-[#E5E7EB] rounded-lg p-6 shadow-sm hover:border-[#7B1A1A]/30 transition-colors ${
                  solution.name === "EvidenceWeb" ? "md:col-span-2" : ""
                }`}
              >
                {/* Logo + name badge */}
                <div className="text-center mb-3">
                  <div className="flex items-center justify-center h-24 mb-3">
                    <img
                      src={logoSrc ?? "/logo.png"}
                      alt={solution.name}
                      className="max-h-16 w-auto object-contain"
                      onError={(e) => { e.currentTarget.src = "/logo.png"; }}
                    />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] border border-[#7B1A1A]/30 px-3 py-1 rounded">
                    {solution.name}
                  </span>
                </div>

                <p className="text-sm font-medium text-[#1F2933] mb-2">{solution.tagline}</p>
                <p className="text-sm text-[#4B5563] leading-relaxed mb-4">{solution.description}</p>
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[#1F2933] uppercase tracking-wide mb-2">Capabilities</h4>
                  <ul className="space-y-1">
                    {solution.capabilities.map((cap) => (
                      <li key={cap} className="flex items-center gap-2 text-sm text-[#4B5563]">
                        <span className="w-1 h-1 rounded-full bg-[#7B1A1A] flex-shrink-0 inline-block" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-4 border-t border-[#E5E7EB]">
                  <p className="text-sm text-[#4B5563] leading-relaxed">
                    <span className="font-semibold text-[#1F2933]">Value: </span>
                    {solution.value}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ecosystem Positioning */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 shadow-sm mb-14">
        <h3 className="text-base font-semibold text-[#1F2933] mb-8">Ecosystem Positioning</h3>
        <div className="grid md:grid-cols-3 gap-8 text-center">
          {[
            { num: "1", label: "Owned Platforms", desc: "AutoGuard, MiCash, Life Happens, Get Me Printing", color: "bg-[#7B1A1A]" },
            { num: "2", label: "Licensed Solutions", desc: "FPI, BOS, LSD, PICME, EvidenceWeb", color: "bg-[#1F2933]" },
            { num: "3", label: "Partner Entities", desc: "Development, marketing, design, infrastructure support", color: "bg-[#4B5563]" },
          ].map((item) => (
            <div key={item.num}>
              <div className={`w-12 h-12 ${item.color} rounded-full mx-auto mb-4 flex items-center justify-center text-white font-semibold`}>
                {item.num}
              </div>
              <h4 className="text-sm font-semibold text-[#1F2933] mb-2">{item.label}</h4>
              <p className="text-sm text-[#4B5563]">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </PortalLayout>
  );
}
